/**
 * 后台管理 API 服务
 */
import request from "./api";

// 获取当前站点ID
function getSiteId(): string {
  return localStorage.getItem('current_site_id') || '';
}

// ==================== 用户管理 ====================

export interface User {
  id: string;
  username: string;
  email: string;
  nickname: string;
  avatar: string;
  phone: string;
  role: string;
  status: "active" | "banned" | "inactive";
  is_vip: boolean;
  created_at: string;
  // 用户自定义字段
  company?: string;
  field_2?: string;
  field_3?: string;
  field_4?: string;
  [key: string]: string | undefined;
}

export interface UsersResponse {
  total: number;
  page: number;
  page_size: number;
  list: User[];
}

/**
 * 获取用户列表
 */
export async function getUsers(params: {
  page?: number;
  page_size?: number;
  role?: string;
  keyword?: string;
}): Promise<UsersResponse> {
  return request(`/admin/users`, {
    params: { ...params, site_id: getSiteId() },
  });
}

/**
 * 修改用户状态
 */
export async function changeUserStatus(
  userId: string,
  status: "active" | "banned" | "inactive"
): Promise<void> {
  return request(`/admin/users/${userId}/status`, {
    method: "PUT",
    body: { status, site_id: getSiteId() },
  });
}

/**
 * 更新用户资料
 */
export async function updateUser(
  userId: string,
  data: {
    email?: string;
    phone?: string;
    password?: string;
  }
): Promise<void> {
  return request(`/admin/users/${userId}`, {
    method: "PUT",
    body: { ...data, site_id: getSiteId() },
  });
}

/**
 * 赠送VIP会员
 */
export async function giftVip(
  userId: string,
  planId: string,
  customDays?: number,
  note?: string
): Promise<void> {
  return request(`/admin/users/${userId}/gift-vip`, {
    method: "POST",
    body: {
      plan_id: planId,
      custom_days: customDays,
      note,
      site_id: getSiteId(),
    },
  });
}

/**
 * 取消VIP会员
 */
export async function cancelVip(userId: string): Promise<void> {
  return request(`/admin/users/${userId}/cancel-vip`, {
    method: "POST",
    body: { site_id: getSiteId() },
  });
}

/**
 * 获取VIP套餐列表
 */
export async function getVipPlans(): Promise<any[]> {
  return request(`/vip/plans`, {
    params: { site_id: getSiteId() },
  });
}

/**
 * 保存VIP套餐列表（逐个更新）
 */
export async function saveVipPlans(plans: any[]): Promise<void> {
  // 后端是单个更新接口，前端需要逐个更新
  const results = await Promise.all(
    plans.map((plan) =>
      request(`/admin/vip/plans/${plan.id}`, {
        method: "PUT",
        body: { ...plan, site_id: getSiteId() },
      })
    )
  );
  return results as any;
}

/**
 * 删除单个VIP套餐
 */
export async function deleteVipPlan(planId: string): Promise<void> {
  return request(`/admin/vip/plans/${planId}`, {
    method: "DELETE",
    body: { site_id: getSiteId() },
  });
}

// ==================== VIP订单 ====================

export interface VipOrder {
  id: string;
  user_id: string;
  plan_id: string;
  plan_name: string;
  amount: number;
  payment_method: string;
  status: 'pending' | 'paid' | 'cancelled';
  created_at: string;
  paid_at: string | null;
}

/**
 * 创建VIP订单
 */
export async function createVipOrder(planId: string, paymentMethod: string): Promise<{ order_id: string }> {
  return request(`/vip/orders`, {
    method: "POST",
    body: { plan_id: planId, payment_method: paymentMethod, site_id: getSiteId() },
  });
}

/**
 * 获取我的VIP状态
 */
export async function getVipStatus(): Promise<{ is_vip: boolean; expires_at: string | null }> {
  return request(`/vip/status`, {
    params: { site_id: getSiteId() },
  });
}

// ==================== 支付配置 ====================

export interface PaymentSettings {
  alipay_enabled: boolean;
  wechat_enabled: boolean;
  qrcode_enabled: boolean;
  qrcode_priority: boolean;
  alipay_app_id: string;
  alipay_private_key: string;
  alipay_public_key: string;
  alipay_notify_url: string;
  alipay_return_url: string;
  alipay_sandbox: boolean;
  wechat_app_id: string;
  wechat_mch_id: string;
  wechat_api_key: string;
  wechat_notify_url: string;
  wechat_sandbox: boolean;
  alipay_qrcode: string;
  wechat_qrcode: string;
  bank_name: string;
  bank_account_name: string;
  bank_account_no: string;
  bank_branch: string;
}

/**
 * 获取支付配置
 */
export async function getPaymentSettings(): Promise<PaymentSettings> {
  return request(`/admin/settings/payment`, {
    params: { site_id: getSiteId() },
  });
}

/**
 * 保存支付配置
 */
export async function savePaymentSettings(settings: Partial<PaymentSettings>): Promise<void> {
  return request(`/admin/settings/payment`, {
    method: "PUT",
    body: { ...settings, site_id: getSiteId() },
  });
}

// ==================== 注册设置 ====================

export type FieldType = "text" | "textarea" | "select" | "radio" | "checkbox" | "image" | "file" | "date" | "number";

export interface RegisterField {
  id: string;
  field_name: string;
  field_label: string;
  field_type: FieldType;
  placeholder: string;
  hint: string;
  options: string[];
  is_required: boolean;
  is_enabled: boolean;
  sort_order: number;
}

/**
 * 获取注册字段列表
 */
export async function getRegisterFields(): Promise<RegisterField[]> {
  return request(`/admin/register-settings/fields`, {
    params: { site_id: getSiteId() },
  });
}

/**
 * 保存注册字段
 */
export async function saveRegisterFields(fields: RegisterField[]): Promise<void> {
  return request(`/admin/register-settings/fields?site_id=${getSiteId()}`, {
    method: "PUT",
    body: { fields },
  });
}

/**
 * 获取注册配置
 */
export async function getRegisterConfig(): Promise<{ fields: RegisterField[] }> {
  return request(`/admin/register-settings/config`, {
    params: { site_id: getSiteId() },
  });
}

// ==================== 短信配置 ====================

export interface SmsConfig {
  enabled: boolean;
  access_key_id: string;
  access_key_secret: string;
  sign_name: string;
  template_code: string;
  region: string;
}

/**
 * 获取短信配置
 */
export async function getSmsConfig(): Promise<SmsConfig> {
  return request(`/admin/register-settings/sms`, {
    params: { site_id: getSiteId() },
  });
}

/**
 * 保存短信配置
 */
export async function saveSmsConfig(config: SmsConfig): Promise<void> {
  return request(`/admin/register-settings/sms`, {
    method: "PUT",
    body: { ...config, site_id: getSiteId() },
  });
}

// ==================== 网站设置 ====================

export interface FooterMenuItem {
  id: string;
  label: string;
  href: string;
  external: boolean;
  enabled: boolean;
}

export interface BannerItem {
  id: string;
  image: string;
  link: string;
  title: string;
  subtitle: string;
  enabled: boolean;
}

export interface SiteSettings {
  site_name: string;
  site_logo: string;
  seo_title: string;
  seo_description: string;
  seo_keywords: string;
  icp: string;
  icp_url: string;
  footer_info: string;
  contact_info: {
    company_name?: string;
    phone?: string;
    email?: string;
    wechat?: string;
    qq?: string;
    address?: string;
    work_hours?: string;
  };
  footer_menus: FooterMenuItem[];
  custom_css: string;
  free_post_limit: number;
  post_charge_per_extra: string;
  search_enabled: boolean;
  search_disabled_message: string;
  banner_settings: BannerItem[];
  enable_vip: boolean;
  enable_topping: boolean;
  hide_company_name: boolean;
  template_id: string;
  theme_color: string;
  login_style: string;
  custom_colors: string[];
}

/**
 * 获取网站设置
 */
export async function getSiteSettings(): Promise<SiteSettings> {
  return request(`/admin/settings`, {
    params: { site_id: getSiteId() },
  });
}

/**
 * 保存网站设置
 */
export async function saveSiteSettings(settings: Partial<SiteSettings>): Promise<void> {
  return request(`/admin/settings`, {
    method: "PUT",
    body: { ...settings, site_id: getSiteId() },
  });
}

// ==================== 信息管理 ====================

export interface Listing {
  id: string;
  title: string;
  status: string;
  view_count: number;
  created_at: string;
  user_nickname?: string;
  category_name?: string;
}

/**
 * 获取信息列表
 */
export async function getListings(params: {
  page?: number;
  page_size?: number;
  status?: string;
  keyword?: string;
}): Promise<{ total: number; page: number; page_size: number; list: Listing[] }> {
  return request(`/admin/listings`, {
    params: { ...params, site_id: getSiteId() },
  });
}

/**
 * 修改信息状态
 */
export async function changeListingStatus(
  listingId: string,
  status: string
): Promise<void> {
  return request(`/admin/listings/${listingId}/status`, {
    method: "PUT",
    body: { status, site_id: getSiteId() },
  });
}

/**
 * 后台发布信息
 */
export async function adminCreateListing(data: {
  category_ids?: string[];
  title: string;
  content: string;
  images?: string[];
  company_name?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_wechat?: string;
  status?: string;
}): Promise<{ id: string }> {
  return request(`/admin/listings`, {
    method: 'POST',
    body: { ...data, site_id: getSiteId() },
  });
}

// ==================== 仪表盘统计 ====================

export interface DashboardStats {
  total_users: number;
  total_listings: number;
  total_views: number;
  today_users: number;
  today_listings: number;
  today_views: number;
  pending_listings: number;
  vip_users: number;
}

export interface CategoryStat {
  name: string;
  count: number;
  percent: number;
}

export interface RecentListing {
  id: string;
  title: string;
  category: string;
  author: string;
  time: string;
  status: string;
  audit_status: string;
}

export interface RecentUser {
  id: string;
  nickname: string;
  email: string;
  phone: string;
  join_time: string;
  listings: number;
  is_vip: boolean;
}

export interface DashboardData {
  stats: {
    total_listings: number;
    total_views: number;
    today_listings: number;
    today_views: number;
    total_users: number;
    today_users: number;
    total_categories: number;
    pending_listings: number;
    pending_reports: number;
    vip_users: number;
  };
  week_trend: { date: string; count: number }[];
  category_stats: CategoryStat[];
  recent_listings: RecentListing[];
  recent_users: RecentUser[];
}

/**
 * 获取仪表盘统计
 */
export async function getDashboardStats(): Promise<DashboardStats> {
  return request(`/admin/dashboard/stats`, {
    params: { site_id: getSiteId() },
  });
}

/**
 * 获取仪表盘完整数据
 */
export async function getDashboardData(): Promise<DashboardData> {
  return request(`/admin/dashboard`, {
    params: { site_id: getSiteId() },
  });
}

// ==================== 分类管理 ====================

export interface Category {
  id: string;
  name: string;
  slug: string;
  parent_id: string | null;
  sort_order: number;
  icon: string;
  icon_color?: string;
  status: 'active' | 'hidden';
  is_nav: string | number;
  children?: Category[];
  created_at?: string;
}

/**
 * 获取分类列表（树形结构）
 */
export async function getCategories(): Promise<Category[]> {
  return request(`/admin/categories`, {
    params: { site_id: getSiteId() },
  });
}

/**
 * 创建分类
 */
export async function createCategory(data: {
  name: string;
  slug?: string;
  parent_id?: string | null;
  sort_order?: number;
  icon?: string;
  status?: 'active' | 'hidden';
}): Promise<Category> {
  return request(`/admin/categories`, {
    method: 'POST',
    body: { ...data, site_id: getSiteId() },
  });
}

/**
 * 更新分类
 */
export async function updateCategory(
  id: string,
  data: {
    name?: string;
    slug?: string;
    parent_id?: string | null;
    sort_order?: number;
    icon?: string;
    status?: 'active' | 'hidden';
  }
): Promise<Category> {
  return request(`/admin/categories/${id}`, {
    method: 'PUT',
    body: { ...data, site_id: getSiteId() },
  });
}

/**
 * 删除分类
 */
export async function deleteCategory(id: string): Promise<void> {
  return request(`/admin/categories/${id}`, {
    method: 'DELETE',
    body: { site_id: getSiteId() },
  });
}

// ==================== 单页管理 ====================

export interface SinglePage {
  id: string;
  title: string;
  slug: string;
  content: string;
  sort_order: number;
  status: 'active' | 'hidden';
  created_at?: string;
  updated_at?: string;
}

/**
 * 获取单页列表
 */
export async function getSinglePages(): Promise<SinglePage[]> {
  return request(`/admin/single-pages`);
}

/**
 * 创建单页
 */
export async function createSinglePage(data: {
  title: string;
  slug?: string;
  content?: string;
  sort_order?: number;
  status?: string;
}): Promise<SinglePage> {
  return request(`/admin/single-pages`, {
    method: 'POST',
    body: { ...data, site_id: getSiteId() },
  });
}

/**
 * 更新单页
 */
export async function updateSinglePage(id: string, data: {
  title?: string;
  slug?: string;
  content?: string;
  sort_order?: number;
  status?: string;
}): Promise<SinglePage> {
  return request(`/admin/single-pages/${id}`, {
    method: 'PUT',
    body: { ...data, site_id: getSiteId() },
  });
}

/**
 * 删除单页
 */
export async function deleteSinglePage(id: string): Promise<void> {
  return request(`/admin/single-pages/${id}`, {
    method: 'DELETE',
    body: { site_id: getSiteId() },
  });
}

// ==================== 导航菜单管理 ====================

export type NavMenuType = 'category' | 'page' | 'link';

export interface NavChild {
  id: string;
  label: string;
  href: string;
  target: '_self' | '_blank';
  enabled: boolean;
}

export interface NavMenu {
  id: string;
  name: string;
  type: NavMenuType;
  target_id: string | null;
  target_url: string;
  sort_order: number;
  status: 'active' | 'inactive';
  icon?: string;
  children?: NavChild[];
  created_at?: string;
}

/**
 * 获取导航菜单列表
 */
export async function getNavMenus(): Promise<NavMenu[]> {
  return request(`/admin/nav-menus`, {
    params: { site_id: getSiteId() },
  });
}

/**
 * 创建导航菜单
 */
export async function createNavMenu(data: {
  name: string;
  type: NavMenuType;
  target_id?: string | null;
  target_url?: string;
  sort_order?: number;
  status?: 'active' | 'inactive';
  icon?: string;
}): Promise<NavMenu> {
  return request(`/admin/nav-menus`, {
    method: 'POST',
    body: { ...data, site_id: getSiteId() },
  });
}

/**
 * 更新导航菜单
 */
export async function updateNavMenu(
  id: string,
  data: {
    name?: string;
    type?: NavMenuType;
    target_id?: string | null;
    target_url?: string;
    sort_order?: number;
    status?: 'active' | 'inactive';
    icon?: string;
  }
): Promise<NavMenu> {
  return request(`/admin/nav-menus/${id}`, {
    method: 'PUT',
    body: { ...data, site_id: getSiteId() },
  });
}

/**
 * 删除导航菜单
 */
export async function deleteNavMenu(id: string): Promise<void> {
  return request(`/admin/nav-menus/${id}`, {
    method: 'DELETE',
    body: { site_id: getSiteId() },
  });
}
